/**
 * One collapsible module section of the notification setup grid — Purchasing,
 * Quality, Documents & Compliance, Onboarding, RFQ, System.
 *
 * Sections come from the catalogue endpoint, so a new domain (or a new event in
 * an existing one) appears here the next time an admin opens the tab, with no
 * hand-maintained list to update.
 *
 * The bulk buttons act on `events` — the rows this section is rendering right
 * now — and never on the domain behind them. With a search active those are not
 * the same set, and a bulk action that reached past the filter changed rows the
 * admin never saw, staged them silently and saved them. So the buttons say how
 * many rows they will touch whenever the header count says the list is narrowed.
 *
 * Retired events are never touched: the "on" count covers live rows only, the
 * bulk buttons skip retired ones, and a section with no live row at all says
 * "All retired" and offers no bulk action. The header counts what the section
 * can list, so while retired rows are hidden they are out of both of its
 * numbers. A retired row holding a staged Reset stays listed, so nothing unsaved
 * is ever out of sight.
 *
 * The bulk buttons only appear on an open section, next to the rows they change.
 *
 * An open section lists topics, not events: most admins decide per topic, and a
 * topic opens to its events for the ones who do not.
 */
import type {
  NotificationChannel,
  NotificationTenantDefaultFrequency,
} from '../../../../lib/api';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../ui/collapsible';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { NotificationTopicRow, TOPIC_GRID } from './NotificationTopicRow';
import { groupByTopic } from './eventGroups';
import { canTurnOn, isEventOn, type TopicChannel } from './eventState';
import {
  type EventConfig,
  type OneDailyDelivery,
  type SetupChoice,
  type SetupEvent,
} from './types';

interface NotificationDomainSectionProps {
  label: string;
  /**
   * Events after the tab's search filter, retired ones included — may be empty.
   * This is the set the section counts and its bulk buttons act on.
   */
  events: SetupEvent[];
  /** Every event in this domain, before the search. */
  allEvents: SetupEvent[];
  /** List retired rows too. */
  showRetired: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isTopicOpen: (topic: string) => boolean;
  onTopicOpenChange: (topic: string, open: boolean) => void;
  canConfigure: boolean;
  frequencyChoices: SetupChoice[];
  /**
   * Tenant-wide cadence every row inherits unless it has one of its own. `''`
   * sends each row to its own platform default instead.
   */
  defaultFrequency: NotificationTenantDefaultFrequency;
  emailsEnabled: boolean;
  /** Batched-mail master switch, for each row's baseline-routing note. */
  digestEnabled: boolean;
  /** What the one daily digest does for this tenant right now. */
  digestDelivery: OneDailyDelivery;
  configFor: (event: SetupEvent) => EventConfig;
  isDirty: (event: SetupEvent) => boolean;
  onUpdate: (event: SetupEvent, patch: Partial<EventConfig>) => void;
  onToggleChannel: (event: SetupEvent, channel: NotificationChannel, on: boolean) => void;
  onReset: (event: SetupEvent) => void;
  /** Stages one enabled state across exactly the rows it is handed. */
  onSetEventsEnabled: (events: SetupEvent[], enabled: boolean) => void;
  /** Stages one topic switch across exactly the rows it is handed. */
  onSetTopicChannel: (events: SetupEvent[], channel: TopicChannel, on: boolean) => void;
}

export function NotificationDomainSection({
  label,
  events,
  allEvents,
  showRetired,
  open,
  onOpenChange,
  isTopicOpen,
  onTopicOpenChange,
  canConfigure,
  frequencyChoices,
  defaultFrequency,
  emailsEnabled,
  digestEnabled,
  digestDelivery,
  configFor,
  isDirty,
  onUpdate,
  onToggleChannel,
  onReset,
  onSetEventsEnabled,
  onSetTopicChannel,
}: NotificationDomainSectionProps) {
  // Retired rows are shown but never counted: they are never sent, and no
  // bulk action can change them, so they belong to neither side of "X of Y".
  const liveEvents = events.filter(canTurnOn);
  const onCount = liveEvents.filter((event) =>
    isEventOn(event, configFor(event), defaultFrequency),
  ).length;
  const allRetired = events.length > 0 && liveEvents.length === 0;
  const dirtyCount = events.filter(isDirty).length;
  const isListed = (event: SetupEvent): boolean =>
    showRetired || canTurnOn(event) || isDirty(event);
  const rows = events.filter(isListed);
  const listed = allEvents.filter(isListed);
  const total = listed.length;
  const topicTotals = new Map(
    groupByTopic(listed).map((topic) => [topic.key, topic.events.length]),
  );
  // A search narrows `rows` but not `total`, so say so — the header read
  // "(3 events) ... 1 of 1 on" otherwise, which looks like a counting bug.
  const filtered = rows.length !== total;
  const hidden = total - rows.length;

  /**
   * "Enable all" only while the section really is showing all of itself. Once a
   * search has narrowed it, the button counts what it will change — live rows
   * only, since retired ones are skipped — so it can never read as a promise
   * about rows scrolled out of sight.
   */
  const bulkLabel = (verb: string): string =>
    filtered ? `${verb} ${liveEvents.length} shown` : `${verb} all`;

  const retiredNote = rows.some((event) => !canTurnOn(event))
    ? ' Retired events are skipped.'
    : '';

  const bulkTitle = (verb: string): string | undefined =>
    filtered
      ? `${verb}s the ${liveEvents.length} event${liveEvents.length === 1 ? '' : 's'} shown here. `
        + `The ${hidden} hidden by your filters ${hidden === 1 ? 'is' : 'are'} left as ${hidden === 1 ? 'it is' : 'they are'}.`
        + retiredNote
      : retiredNote.trim() || undefined;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange} className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex flex-1 items-center gap-2 text-left">
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span className="min-w-[230px] font-semibold">{label}</span>
            <span className="min-w-[110px] text-sm text-muted-foreground">
              {filtered
                ? `${rows.length} of ${total} events`
                : `${total} event${total === 1 ? '' : 's'}`}
            </span>
            <span className="text-sm text-muted-foreground">
              {allRetired && !filtered && 'All retired'}
              {allRetired && filtered && (rows.length ? 'All shown retired' : 'All matches retired')}
              {!allRetired && (filtered
                ? `${onCount} of ${liveEvents.length} shown on`
                : `${onCount} on`)}
            </span>
            {dirtyCount > 0 && <Badge variant="secondary">{dirtyCount} unsaved</Badge>}
          </button>
        </CollapsibleTrigger>
        {open && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canConfigure || liveEvents.length === 0}
              title={bulkTitle('Enable')}
              onClick={() => onSetEventsEnabled(liveEvents, true)}
            >
              {bulkLabel('Enable')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canConfigure || liveEvents.length === 0}
              title={bulkTitle('Disable')}
              onClick={() => onSetEventsEnabled(liveEvents, false)}
            >
              {bulkLabel('Disable')}
            </Button>
          </div>
        )}
      </div>

      <CollapsibleContent>
        {events.length === 0 && (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            No notifications in {label} match your filters.
          </p>
        )}
        {events.length > 0 && rows.length === 0 && (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            Only retired events here. Use Show retired to list them.
          </p>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <div className="min-w-[680px]">
              <div
                className={`${TOPIC_GRID} border-t bg-muted/40 py-1 text-xs font-semibold text-muted-foreground`}
              >
                <span>Topic</span>
                <span>Reaches</span>
                <span className="text-center">Bell</span>
                <span className="text-center">Email</span>
                <span>On</span>
                <span />
              </div>
              {groupByTopic(rows).map((topic) => (
                <NotificationTopicRow
                  key={topic.key}
                  topic={topic}
                  total={topicTotals.get(topic.key) ?? topic.events.length}
                  open={isTopicOpen(topic.key)}
                  onOpenChange={(next) => onTopicOpenChange(topic.key, next)}
                  canConfigure={canConfigure}
                  frequencyChoices={frequencyChoices}
                  defaultFrequency={defaultFrequency}
                  emailsEnabled={emailsEnabled}
                  digestEnabled={digestEnabled}
                  digestDelivery={digestDelivery}
                  configFor={configFor}
                  isDirty={isDirty}
                  onUpdate={onUpdate}
                  onToggleChannel={onToggleChannel}
                  onReset={onReset}
                  onSetTopicChannel={onSetTopicChannel}
                />
              ))}
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
